<template>
	<div class="cart m-4">
		<div v-if="!cart.length" class="w-full flex">
			<h1 class="text-2xl mt-12 m-auto">
				Vous n'avez rien dans votre panier
			</h1>
		</div>
		<rp-card
			v-else
			v-for="bd in cart"
			:key="bd.index"
			class="w-full p-4 mb-6"
			horizontal
		>
			<template #header>
				<div class="flex flex-col items-center mr-4">
					<img class="mb-2 w-48 object-contain" :src="bd.cover.url" alt="" />
				</div>
			</template>
			<div class="w-full">
				<h1 class="text-xl">{{ bd.title[0].text }}</h1>
				<h2 class="text-sm text-gray-600 mb-2">BD</h2>
				<p v-if="bd.description[0]" class="text-md text-gray-800">
					{{ bd.description[0].text }}
				</p>
			</div>
			<template #footer>
				<div class="flex justify-between items-center">
					<RpAddRemoveInput class="mr-8" :min="1" v-model="bd.quantity" />
					<span class="text-xl"
						>{{ (bd.price * bd.quantity).toFixed(2) }}€</span
					>
					<rp-button
						class="ml-12 mr-4 bg-opacity-0"
						typoColor="black"
						hoverColor="gray-300"
						@click="delItem(bd)"
					>
						<svg viewBox="0 0 20 20" fill="currentColor" class="x w-6 h-6">
							<path
								fill-rule="evenodd"
								d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
								clip-rule="evenodd"
							></path>
						</svg>
					</rp-button>
				</div>
			</template>
		</rp-card>

		<rp-card v-if="cart.length" class="mt-2 mb-2 w-64 mt-auto ml-auto">
			<template #header>
				<div>
					<div>Veuillez choisir votre moyen de livraison :</div>
					<div class="inline-block relative w-full my-4">
						<select
							v-model="senderChoice"
							class="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
						>
							<option value="" disabled selected>Moyen de livraison</option>
							<option value="collecte">Collecte</option>
							<option value="france">France</option>
							<option value="monde">Europe et monde</option>
						</select>
						<div
							class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
						>
							<svg
								class="fill-current h-4 w-4"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path
									d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
								/>
							</svg>
						</div>
					</div>
					<div class="flex justify-between items-center">
						<span class="text-md">Panier</span>
						<span class="text-lg">{{ totalCart }}€</span>
					</div>
					<div class="flex justify-between items-center mb-2">
						<span class="text-md">Frais de livraison</span>
						<span class="text-lg">{{ deliveryCharges }}€</span>
					</div>
					<div class="flex justify-between items-center mb-1">
						<span class="text-lg">Total</span>
						<span class="text-2xl">{{ total }}€</span>
					</div>
				</div>
			</template>
			<rp-button @click="isModalVisible = true" :disabled="!senderChoice"
				>Payer</rp-button
			>
		</rp-card>

		<rp-modal v-if="isModalVisible" noFooter @close="isModalVisible = false">
			<template #header>
				<h1 class="text-2xl">Paiement</h1>
			</template>
			<rp-alert v-if="emailSent" @close="emailSent = false" level="success">
				L'email a bien été envoyé
			</rp-alert>
			<rp-alert
				v-if="paymentCompleted"
				@close="paymentCompleted = false"
				level="success"
			>
				Le paiement a bien été envoyé
			</rp-alert>
			<rp-alert
				v-if="paymentCancelled"
				@close="paymentCancelled = false"
				level="error"
			>
				Le paiement a échoué
			</rp-alert>

			<rp-alert v-if="paymentType && alertText" level="info" noClose>
				{{ alertText }}
			</rp-alert>
			<div>Veuillez choisir votre moyen de paiement :</div>
			<div class="inline-block relative w-full my-4">
				<select
					v-model="paymentType"
					class="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
				>
					<option value="" disabled selected>Moyen de paiement</option>
					<option value="paypal">Carte Bancaire / Paypal</option>
					<option value="cheque">Chèque</option>
				</select>
				<div
					class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
				>
					<svg
						class="fill-current h-4 w-4"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
					>
						<path
							d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
						/>
					</svg>
				</div>
			</div>
			<div v-if="paymentType === 'paypal'" class="my-4">
				Veuillez remplir le formulaire ci-dessous, l'envoyer puis régler la
				somme de <strong>{{ total }}€</strong> en cliquant sur le bouton paypal suivant
				:
				<div class="flex w-full justify-center">
					<PayPal
						class="mt-4"
						:amount="total"
						currency="EUR"
						:client="credentials"
						:button-style="paypalButtonStyle"
						:env="paypalEnv"
						@payment-completed="paymentCompleted = true"
						@payment-cancelled="paymentCancelled = true"
					>
					</PayPal>
				</div>
			</div>
			<div v-else-if="paymentType === 'cheque'" class="my-4">
				<p>
					Veuillez remplir le formulaire ci-dessous et régler la somme de
					<strong>{{ total }}€</strong> en l'envoyant par cheque a cette adresse
					:
				</p>
				<pre>

Atelier Fabien Rypert
34, rue d'ham
62190 Lillers
				</pre
				>
			</div>
			<form v-if="paymentType" class="mt-2" @submit.prevent="sendMail">
				<label
					class="block text-gray-700 text-sm font-bold mb-2"
					for="username"
				>
					Email
				</label>
				<input
					aria-label="Email"
					name="email"
					type="email"
					required
					class="mb-4 appearance-none rounded-none w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:shadow-outline-rypert-primary focus:border-rypert-primary focus:z-10 sm:text-sm sm:leading-5"
					placeholder="Email"
				/>

				<label
					class="block text-gray-700 text-sm font-bold mb-2"
					for="username"
				>
					Nom
				</label>
				<input
					aria-label="Name"
					name="name"
					type="text"
					required
					class="mb-4 appearance-none rounded-none w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:shadow-outline-rypert-primary focus:border-rypert-primary focus:z-10 sm:text-sm sm:leading-5"
					placeholder="Votre nom"
				/>
				<label class="block text-gray-700 text-sm font-bold mb-2" for="address">
					Adresse
				</label>
				<textarea
					aria-label="Votre adresse"
					name="address"
					rows="5"
					required
					class="appearance-none rounded-none w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:shadow-outline-rypert-primary focus:border-rypert-primary focus:z-10 sm:text-sm sm:leading-5"
					placeholder="Votre adresse"
				></textarea>
				<input name="total" type="hidden" :value="total" />
				<input name="cartList" type="hidden" :value="cartListFormated" />
				<input name="paymentType" type="hidden" :value="paymentType" />
				<input name="senderChoice" type="hidden" :value="senderChoice" />
				<input name="emailTo" type="hidden" :value="emailTo" />
				<rp-button class="mt-4 w-full" :disabled="sending">
					Envoyer
				</rp-button>
			</form>
		</rp-modal>
	</div>
</template>

<script>
import RpCard from "../components/RpCard";
import RpButton from "../components/RpButton";
import RpAddRemoveInput from "../components/RpAddRemoveInput";
import RpModal from "../components/RpModal";
import RpAlert from "../components/RpAlert";
import PayPal from "vue-paypal-checkout";
import emailjs from "emailjs-com";

export default {
	name: "Cart",
	components: {
		RpCard,
		RpButton,
		RpAddRemoveInput,
		RpModal,
		RpAlert,
		PayPal,
	},
	data() {
		return {
			isModalVisible: false,
			paymentType: "",
			senderChoice: "",
			sending: false,
			emailSent: false,
			paymentCompleted: false,
			paymentCancelled: false,
			paypalStep: 0,
			credentials: {
				sandbox: process.env.VUE_APP_PAYPAL_SANDBOX,
				production: process.env.VUE_APP_PAYPAL_PRODUCTION,
			},
			paypalButtonStyle: {
				shape: "rect",
				color: "gold",
			},
		};
	},
	computed: {
		deliveryCharges() {
			let quantity = 0;
			this.cart.forEach((bd) => {
				quantity += bd.quantity;
			});
			/* eslint-disable indent */
			switch (this.senderChoice) {
				case "monde":
					switch (quantity) {
						case 1:
							return (2.99).toFixed(2);
						case 2:
							return (1.99).toFixed(2);
						default:
							return (0.0).toFixed(2);
					}
				case "france":
					switch (quantity) {
						case 1:
							return (4.99).toFixed(2);
						case 2:
							return (3.99).toFixed(2);
						case 3:
							return (2.99).toFixed(2);
						default:
							return (0.0).toFixed(2);
					}
				default:
					return (0.0).toFixed(2);
			}
		},
		totalCart() {
			let total = 0;
			this.cart.forEach((bd) => {
				total += bd.quantity * bd.price;
			});
			return total.toFixed(2);
		},
		total() {
			return (Number(this.totalCart) + Number(this.deliveryCharges)).toFixed(2);
		},
		cartListFormated() {
			let cartList = "";
			this.cart.forEach((el) => {
				cartList += `- ${el.quantity}x ${el.title[0].text}<br>`;
			});
			return cartList;
		},
		cart() {
			return this.$store.state.myArticles;
		},
		emailTo() {
			return process.env.VUE_APP_EMAIL;
		},
		paypalEnv() {
			return process.env.NODE_ENV === "production" ? "production" : "sandbox";
		},
		alertText() {
			if (this.senderChoice === "collecte") {
				return "";
			} else if (this.paymentType === "paypal") {
				return "Le traitement de la commande se fait le jour même. Les articles sont envoyés le lendemain ou le jour même pour les commandes passées avant midi. Par Mondial Relay ou par la Poste.";
			} else if (this.paymentType === "cheque") {
				return "Le traitement de la commande prendra le temps de l'encaissement du chèque. Une fois cela fait les articles sont envoyés le lendemain ou le jour meme pour les commandes passées avant midi. Par Mondial Relay ou par la Poste.";
			}
			return "";
		},
	},
	methods: {
		delItem(article) {
			this.$store.commit("removeArticle", article);
		},
		sendMail(e) {
			this.sending = true;
			console.log(process.env);
			emailjs
				.sendForm("gmail", "cheque", e.target, process.env.VUE_APP_MAILJS)
				.then(
					(result) => {
						this.sending = false;
						this.emailSent = true;
						if (this.paymentType === "cheque") {
							this.$store.commit("emptyCart");
						}
						console.log("SUCCESS!", result.status, result.text);
					},
					(error) => {
						this.sending = false;
						console.log("FAILED...", error);
					}
				);
		},
	},
};
</script>

<style></style>
